import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Add as AddIcon
} from '@mui/icons-material';
import apiService from 'Network/apiService';
import { API_ENDPOINTS } from 'Network/apiEndpoints';
import { useToast } from 'OtherReusableFunctionality/ToastContext';

const getProductSizes = (product) => {
  if (!product) return [];
  return product.product_sizes || product.sizes || product.ProductSize || [];
};

const getSizeList = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.sizes)) return data.sizes;
  if (Array.isArray(data?.ProductSize)) return data.ProductSize;
  if (Array.isArray(data?.product_sizes)) return data.product_sizes;
  if (Array.isArray(data?.product_size)) return data.product_size;
  return [];
};

const getStockList = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.ProductStock)) return data.ProductStock;
  if (Array.isArray(data?.product_stock)) return data.product_stock;
  return [];
};

const getSizeId = (sizeItem) => sizeItem?.id ?? sizeItem?.product_size_id ?? sizeItem?.size_id;

/** Prefer real ProductSize FK; fall back to nested size object for amount matching. */
const getStockSizeId = (stockItem) => {
  if (!stockItem) return null;

  if (stockItem.product_size != null && typeof stockItem.product_size === 'object') {
    return stockItem.product_size.id
      ?? stockItem.product_size.product_size_id
      ?? stockItem.product_size_id
      ?? stockItem.product_size;
  }

  return stockItem.product_size_id
    ?? stockItem.product_size
    ?? null;
};

const toInteger = (value) => {
  const parsed = parseInt(String(value ?? '').trim(), 10);
  return Number.isNaN(parsed) ? '' : String(parsed);
};

/**
 * Resolve ProductSize primary key (same value Add Stock sends).
 * Only returns a PK that exists in sizesList when that list is available.
 */
const resolveProductSizePk = (value, sizesList = []) => {
  if (value != null && typeof value === 'object') {
    const objectId = value.id ?? value.product_size_id ?? value.size_id;
    if (objectId != null) {
      value = objectId;
    } else if (value.size != null) {
      value = value.size;
    } else {
      return '';
    }
  }

  const asInt = toInteger(value);
  if (!asInt && value !== 0 && value !== '0') return '';

  // Without sizes we cannot safely tell a size amount from a ProductSize PK
  if (!Array.isArray(sizesList) || sizesList.length === 0) {
    return '';
  }

  const byId = sizesList.find((item) => toInteger(getSizeId(item)) === asInt);
  if (byId) return toInteger(getSizeId(byId));

  const bySize = sizesList.find(
    (item) => toInteger(item.size) === asInt || String(item.size) === String(value)
  );
  if (bySize) return toInteger(getSizeId(bySize));

  // Do not send size amount / unknown ids as ProductSize PK
  return '';
};

const normalizeBatch = (value) => {
  if (value == null || value === 'null' || value === 'undefined') return '';
  return String(value).trim();
};

const ManageProductSizesModal = ({ open, onClose, product, onSizesUpdated }) => {
  const toast = useToast();
  const [error, setError] = useState('');
  const [stocks, setStocks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingStockId, setDeletingStockId] = useState(null);
  const [stockToDelete, setStockToDelete] = useState(null);
  const [editingStockId, setEditingStockId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({
    product_size: '',
    quantity: '',
    min_threshold: '',
    is_unlimited: false,
    batch: ''
  });
  const [editForm, setEditForm] = useState({
    quantity: '',
    batch: '',
    is_unlimited: false,
    product_size: ''
  });
  const [sizes, setSizes] = useState([]);

  const fetchModalData = useCallback(async () => {
    if (!product?.id) return;

    setIsLoading(true);
    setError('');

    try {
      const [stockResponse, sizesResponse] = await Promise.all([
        apiService.get(`${API_ENDPOINTS.GET_product_stock_by_product_id}?product_id=${product.id}`),
        apiService.get(`${API_ENDPOINTS.GET_product_size_by_id}?product_id=${product.id}`)
      ]);

      if (stockResponse?.success) {
        setStocks(getStockList(stockResponse));
      } else {
        setStocks([]);
        setError(stockResponse?.message || 'Failed to fetch product stock');
      }

      if (sizesResponse?.success) {
        setSizes(getSizeList(sizesResponse));
      } else {
        setSizes(getProductSizes(product));
      }
    } catch (fetchError) {
      console.error('Error fetching product data:', fetchError);
      setStocks([]);
      setSizes(getProductSizes(product));
      setError(fetchError?.message || 'An error occurred while fetching product data');
    } finally {
      setIsLoading(false);
    }
  }, [product]);

  useEffect(() => {
    if (!open) {
      setEditingStockId(null);
      setShowAddForm(false);
      setError('');
      setStocks([]);
      setSizes([]);
      setAddForm({ product_size: '', quantity: '', min_threshold: '', is_unlimited: false, batch: '' });
      setEditForm({ quantity: '', batch: '', is_unlimited: false, product_size: '' });
      setStockToDelete(null);
      return;
    }

    fetchModalData();
  }, [open, fetchModalData]);

  const getSizeLabel = (sizeId) => {
    const hasValidSizeValue = (value) =>
      value != null && value !== '' && String(value) !== 'undefined' && String(value) !== 'null';

    if (sizeId != null && typeof sizeId === 'object') {
      if (!hasValidSizeValue(sizeId.size) && !hasValidSizeValue(sizeId.id)) {
        return '';
      }
      const sizeValue = sizeId.size ?? sizeId.id;
      const unit = sizeId.unit_of_measure || product?.unit_of_measure || '';
      return `Size : ${sizeValue}${unit ? ` ${unit}` : ''}`.trim();
    }

    if (!hasValidSizeValue(sizeId)) {
      return '';
    }

    const sizeItem = sizes.find(
      (item) =>
        getSizeId(item)?.toString() === sizeId?.toString()
        || toInteger(item.size) === toInteger(sizeId)
        || String(item.size) === String(sizeId)
    );

    if (sizeItem) {
      const unit = sizeItem.unit_of_measure || product?.unit_of_measure || '';
      return `Size : ${sizeItem.size}${unit ? ` ${unit}` : ''}`.trim();
    }

    const unit = product?.unit_of_measure || '';
    return `Size : ${sizeId}${unit ? ` ${unit}` : ''}`.trim();
  };

  const handleStartAdd = (sizeId = '') => {
    setShowAddForm(true);
    setEditingStockId(null);
    setAddForm({
      product_size: sizeId?.toString() || '',
      quantity: '',
      min_threshold: '',
      is_unlimited: false,
      batch: ''
    });
    setError('');
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setAddForm({ product_size: '', quantity: '', min_threshold: '', is_unlimited: false, batch: '' });
    setError('');
  };

  const handleAddStock = async () => {
    if (!product?.id) return;

    if (!addForm.is_unlimited && !addForm.quantity) {
      setError('Quantity is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const submitData = new FormData();
      submitData.append('product', product.id);
      if (addForm.product_size) {
        submitData.append('product_size', resolveProductSizePk(addForm.product_size, sizes));
      }
      submitData.append('quantity', toInteger(addForm.quantity) || '0');
      submitData.append('min_threshold', toInteger(addForm.min_threshold) || '0');
      submitData.append('is_unlimited', addForm.is_unlimited ? 'true' : 'false');
      submitData.append('batch', normalizeBatch(addForm.batch));

      const response = await apiService.postMultipart(
        API_ENDPOINTS.POST_add_product_stock,
        submitData
      );

      if (response?.success) {
        toast.showSuccess(response?.message || 'Product stock added successfully!');
        handleCancelAdd();
        await fetchModalData();
        onSizesUpdated?.();
      } else {
        setError(response?.message || response?.error || 'Failed to add product stock');
      }
    } catch (addError) {
      console.error('Error adding product stock:', addError);
      setError(
        addError?.message
        || addError?.error
        || (typeof addError === 'string' ? addError : null)
        || 'An error occurred while adding product stock'
      );    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartEdit = async (stockItem) => {
    setEditingStockId(stockItem.id);
    setShowAddForm(false);
    setError('');

    const fallbackSizePk = resolveProductSizePk(getStockSizeId(stockItem), sizes);

    try {
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_product_stock_by_id}?product_stock_id=${stockItem.id}`
      );

      const stockData = response?.data?.ProductStock?.[0]
        || response?.data?.product_stock
        || response?.data
        || stockItem;

      const productSizePk = resolveProductSizePk(
        getStockSizeId(stockData) ?? getStockSizeId(stockItem),
        sizes
      ) || fallbackSizePk;

      setEditForm({
        quantity: stockData?.quantity?.toString() || stockItem?.quantity?.toString() || '',
        batch: normalizeBatch(stockData?.batch ?? stockItem?.batch),
        is_unlimited: Boolean(stockData?.is_unlimited ?? stockItem?.is_unlimited),
        product_size: productSizePk
      });
    } catch (fetchError) {
      console.error('Error fetching product stock details:', fetchError);
      setEditForm({
        quantity: stockItem?.quantity?.toString() || '',
        batch: normalizeBatch(stockItem?.batch),
        is_unlimited: Boolean(stockItem?.is_unlimited),
        product_size: fallbackSizePk
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingStockId(null);
    setEditForm({ quantity: '', batch: '', is_unlimited: false, product_size: '' });
    setError('');
  };

  const handleUpdateStock = async (stockItem) => {
    if (!product?.id || !stockItem?.id) return;

    if (!editForm.is_unlimited && !editForm.quantity) {
      setError('Quantity is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const productSizePk = resolveProductSizePk(
        editForm.product_size || getStockSizeId(stockItem),
        sizes
      );

      const submitData = new FormData();
      submitData.append('product_stock_id', stockItem.id);
      submitData.append('product', product.id);
      // Only send product_size when we have a real ProductSize PK (omit for size-less stock)
      if (productSizePk) {
        submitData.append('product_size', productSizePk);
      }
      submitData.append('quantity', editForm.is_unlimited ? '0' : (toInteger(editForm.quantity) || '0'));
      submitData.append('batch', normalizeBatch(editForm.batch));
      submitData.append('is_unlimited', editForm.is_unlimited ? 'true' : 'false');

      const response = await apiService.putMultipart(
        API_ENDPOINTS.PUT_update_product_stock,
        submitData
      );

      if (response?.success) {
        toast.showSuccess(response?.message || 'Product stock updated successfully!');
        handleCancelEdit();
        await fetchModalData();
        onSizesUpdated?.();
      } else {
        setError(response?.message || 'Failed to update product stock');
      }
    } catch (updateError) {
      console.error('Error updating product stock:', updateError);
      const fieldErrors = updateError?.errors
        ? Object.values(updateError.errors).flat().filter(Boolean).join(' ')
        : '';
      setError(fieldErrors || updateError?.message || 'An error occurred while updating product stock');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteStock = (stockItem) => {
    if (!stockItem?.id) return;
    setStockToDelete(stockItem);
  };

  const handleCloseDeleteConfirm = () => {
    setStockToDelete(null);
  };

  const handleConfirmDeleteStock = async () => {
    const stockItem = stockToDelete;
    if (!stockItem?.id) return;

    setDeletingStockId(stockItem.id);
    setError('');

    try {
      const submitData = new FormData();
      submitData.append('product_stock_id', stockItem.id);

      const token = apiService.getAccessToken();
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}${API_ENDPOINTS.DELETE_product_stock}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: submitData
        }
      );

      const result = await response.json();

      if (result?.success) {
        toast.showSuccess(result?.message || 'Product stock deleted successfully!');
        if (editingStockId === stockItem.id) {
          handleCancelEdit();
        }
        setStockToDelete(null);
        await fetchModalData();
        onSizesUpdated?.();
      } else {
        setError(result?.message || 'Failed to delete product stock');
      }
    } catch (deleteError) {
      console.error('Error deleting product stock:', deleteError);
      setError(deleteError?.message || 'An error occurred while deleting product stock');
    } finally {
      setDeletingStockId(null);
    }
  };

  if (!product) return null;

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Add Stock</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage stock for {product.name}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!showAddForm && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleStartAdd()}
              disabled={isSubmitting || isLoading}
            >
              Add Stock
            </Button>
          </Box>
        )}

        {showAddForm && (
          <Box sx={{ mt: 1, mb: 3, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Add Stock
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      checked={addForm.is_unlimited}
                      onChange={(e) => setAddForm((prev) => ({ ...prev, is_unlimited: e.target.checked }))}
                    />
                  }
                  label="Unlimited Stock"
                />
              </Grid>
              {sizes.length > 0 && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Product Size</InputLabel>
                    <Select
                      value={addForm.product_size}
                      label="Product Size"
                      onChange={(e) => setAddForm((prev) => ({ ...prev, product_size: String(e.target.value) }))}
                    >
                      {sizes.map((sizeItem) => {
                        const sizeId = getSizeId(sizeItem);
                        const sizeValue = toInteger(sizeItem.size) || sizeItem.size;
                        return (
                          <MenuItem key={sizeId} value={String(sizeId)}>
                            {sizeValue} {sizeItem.unit_of_measure || product.unit_of_measure || ''}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Batch"
                  value={addForm.batch}
                  onChange={(e) => setAddForm((prev) => ({ ...prev, batch: e.target.value }))}
                />
              </Grid>
              {!addForm.is_unlimited && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Quantity"
                    type="number"
                    value={addForm.quantity}
                    onChange={(e) => setAddForm((prev) => ({ ...prev, quantity: e.target.value }))}
                    inputProps={{ onWheel: (e) => e.currentTarget.blur() }}
                  />
                </Grid>
              )}
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button onClick={handleCancelAdd} color="inherit" disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleAddStock}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} /> : <SaveIcon />}
              >
                Save Stock
              </Button>
            </Box>
          </Box>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : stocks.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No stock records found for this product yet.
          </Alert>
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ mb: 2, borderRadius: 2 }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                width: '100%',
                '& .MuiTableCell-root:first-of-type': {
                  pl: '28px !important'
                },
                '& .MuiTableCell-root:last-of-type:not([colspan])': {
                  pr: '20px !important',
                  textAlign: 'right !important',
                  width: 110,
                  whiteSpace: 'nowrap'
                }
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Size</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', pl: '70px !important' }}>Batch</TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, whiteSpace: 'nowrap', pr: '150px !important' }}
                  >
                    Quantity
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stocks.map((stockItem) => {
                  const isEditing = editingStockId === stockItem.id;
                  const sizeId = getStockSizeId(stockItem);
                  const sizeLabel = getSizeLabel(sizeId);
                  const sizeDisplay = (sizeLabel || '').replace(/^Size\s*:\s*/i, '') || 'Stock';
                  const batchValue = normalizeBatch(stockItem.batch);

                  if (isEditing) {
                    return (
                      <TableRow key={stockItem.id}>
                        <TableCell colSpan={4} sx={{ py: 2, bgcolor: 'action.hover' }}>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            {sizeLabel ? `Edit Stock - ${sizeDisplay}` : 'Edit Stock'}
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                  <Checkbox
                                    checked={editForm.is_unlimited}
                                    onChange={(e) => setEditForm((prev) => ({
                                      ...prev,
                                      is_unlimited: e.target.checked
                                    }))}
                                  />
                                }
                                label="Unlimited Stock"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                required={!editForm.is_unlimited}
                                label="Quantity"
                                type="number"
                                value={editForm.quantity}
                                disabled={editForm.is_unlimited}
                                onChange={(e) => setEditForm((prev) => ({
                                  ...prev,
                                  quantity: e.target.value,
                                  is_unlimited: false
                                }))}
                                inputProps={{ onWheel: (e) => e.currentTarget.blur() }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Batch"
                                value={editForm.batch}
                                onChange={(e) => setEditForm((prev) => ({ ...prev, batch: e.target.value }))}
                              />
                            </Grid>
                          </Grid>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                            <Button onClick={handleCancelEdit} color="inherit" disabled={isSubmitting}>
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={() => handleUpdateStock(stockItem)}
                              disabled={isSubmitting}
                              startIcon={isSubmitting ? <CircularProgress size={16} /> : <SaveIcon />}
                            >
                              Update
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <TableRow
                      key={stockItem.id}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', pl: 2.5 }}>
                        {sizeDisplay}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', pl: '75px !important' }}>
                        {batchValue || '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ pr: '150px !important' }}>
                        {stockItem.is_unlimited ? 'Unlimited' : (stockItem.quantity ?? 0)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'inline-flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                          <Tooltip title="Edit">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleStartEdit(stockItem)}
                                disabled={Boolean(deletingStockId) || isSubmitting}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteStock(stockItem)}
                                disabled={deletingStockId === stockItem.id || isSubmitting}
                              >
                                {deletingStockId === stockItem.id ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <DeleteIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Add, update, or remove stock entries for each product size.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end' }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={Boolean(stockToDelete)} onClose={handleCloseDeleteConfirm} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Delete Stock</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary">
          Are you sure you want to delete stock for{' '}
          <strong>
            {(stockToDelete && getSizeLabel(getStockSizeId(stockToDelete))) || 'this item'}
          </strong>
          ?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCloseDeleteConfirm} color="inherit" disabled={Boolean(deletingStockId)}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirmDeleteStock}
          color="error"
          variant="contained"
          disabled={Boolean(deletingStockId)}
          startIcon={deletingStockId ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default ManageProductSizesModal;
